/**
 * Regalo automático en carrito.
 * Detecta >= N unidades de un grupo de productos y añade la silueta de regalo
 * (gratis vía el descuento "Compra X y obtén Y" de Shopify).
 *
 * Spec: docs/superpowers/specs/2026-06-18-regalo-automatico-carrito-design.md
 *
 * Archivo dual-mode: en el navegador registra el custom element <gift-auto-add>;
 * en Node (tests) sólo exporta las funciones puras.
 */

// ----------------------- Funciones puras (testeables) -----------------------

// IDs de Shopify se comparan como STRING para evitar pérdida de precisión de
// enteros grandes (> 2^53) al pasar por JSON.parse / Number.

function countUnits(items, productIds) {
    if (!Array.isArray(items) || !Array.isArray(productIds) || productIds.length === 0) {
        return 0;
    }
    const ids = new Set(productIds.map(String));
    let total = 0;
    for (const item of items) {
        if (ids.has(String(item.product_id))) {
            total += Number(item.quantity) || 0;
        }
    }
    return total;
}

function giftInCart(items, giftProductId) {
    if (!Array.isArray(items)) return false;
    const gid = String(giftProductId);
    return items.some((item) => String(item.product_id) === gid);
}

/**
 * Decide la acción para un grupo según el estado del carrito.
 * @param {{units:number, threshold:number, hasGift:boolean, armed:boolean}} state
 * @returns {{action:'add'|'none', armed:boolean}}
 */
function evaluateGroup({ units, threshold, hasGift, armed }) {
    if (units >= threshold) {
        if (armed && !hasGift) {
            return { action: 'add', armed: false };
        }
        return { action: 'none', armed: false };
    }
    return { action: 'none', armed: true };
}

// ----------------------- Lado navegador (custom element) -----------------------

if (typeof window !== 'undefined' && window.customElements && typeof HTMLElement !== 'undefined') {
    class GiftAutoAdd extends HTMLElement {
        connectedCallback() {
            // Guard B2B (Sparklayer): no actuar en sesiones mayoristas.
            if (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.isB2B) {
                return;
            }

            this.groups = this.#readConfig();
            if (!this.groups.length) return;

            this.popupTitle = this.getAttribute('data-popup-title') || '🎁 ¡Has conseguido tu regalo!';
            this.popupButton = this.getAttribute('data-popup-button') || 'Seguir comprando';
            this.popupFree = this.getAttribute('data-popup-free') || '¡GRATIS!';
            this.popupExtraText = this.getAttribute('data-popup-extra-text') || '';
            this.busy = false;
            this.debounceTimer = null;
            this.onCartChange = (event) => this.#scheduleEvaluate(event && event.detail ? event.detail.cart : null);
            // cart:refresh no lleva detail.cart → se re-evalúa contra el servidor.
            // Cubre rutas donde el theme refresca el carrito sin emitir cart:change
            // (p. ej. cambios de cantidad en la página /cart).
            this.onCartRefresh = () => this.#scheduleEvaluate(null);

            document.addEventListener('cart:change', this.onCartChange);
            document.addEventListener('cart:refresh', this.onCartRefresh);

            // Evaluación inicial con el estado actual del carrito.
            this.#evaluateFromServer();
        }

        disconnectedCallback() {
            if (this.onCartChange) document.removeEventListener('cart:change', this.onCartChange);
            if (this.onCartRefresh) document.removeEventListener('cart:refresh', this.onCartRefresh);
            if (this.debounceTimer) clearTimeout(this.debounceTimer);
            this.#closeModal(false);
        }

        #readConfig() {
            const node = this.querySelector('[data-gift-auto-add-config]');
            if (!node) return [];
            try {
                const parsed = JSON.parse(node.textContent);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                console.warn('[gift-auto-add] config JSON inválido', e);
                return [];
            }
        }

        #scheduleEvaluate(cart) {
            if (this.debounceTimer) clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                if (cart && Array.isArray(cart.items)) {
                    this.#evaluate(cart.items);
                } else {
                    this.#evaluateFromServer();
                }
            }, 120);
        }

        async #evaluateFromServer() {
            try {
                const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
                const cart = await (await fetch(`${root}cart.js`)).json();
                this.#evaluate(cart.items || []);
            } catch (e) {
                console.warn('[gift-auto-add] no se pudo leer el carrito', e);
            }
        }

        async #evaluate(items) {
            if (this.busy) return;
            this.busy = true;
            const addedGifts = [];
            try {
                let workingItems = items;
                for (const group of this.groups) {
                    const units = countUnits(workingItems, group.productIds);
                    const hasGift = giftInCart(workingItems, group.giftProductId);
                    const armed = this.#getArmed(group.id);
                    const result = evaluateGroup({ units, threshold: group.threshold, hasGift, armed });

                    if (result.action === 'add') {
                        const ok = await this.#addGift(group);
                        if (ok) {
                            // Sólo desarmamos tras un add confirmado.
                            // Spec: si el add falla, NO se toca armed (se reintenta en el próximo evento).
                            this.#setArmed(group.id, false);
                            addedGifts.push(group);
                            // Reflejar el regalo en el carrito local para no recontar.
                            workingItems = workingItems.concat([{ product_id: group.giftProductId, quantity: 1 }]);
                        }
                    } else {
                        this.#setArmed(group.id, result.armed);
                    }
                }
                if (addedGifts.length) {
                    this.#showModal(addedGifts);
                    document.dispatchEvent(new CustomEvent('cart:refresh'));
                }
            } finally {
                this.busy = false;
            }
        }

        async #addGift(group) {
            try {
                const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
                const res = await fetch(`${root}cart/add.js`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items: [{ id: group.giftVariantId, quantity: 1 }] }),
                });
                if (!res.ok) throw new Error(`add.js respondió ${res.status}`);
                return true;
            } catch (e) {
                console.warn('[gift-auto-add] no se pudo añadir el regalo', group.name, e);
                return false;
            }
        }

        #getArmed(groupId) {
            try {
                const v = sessionStorage.getItem(`gaa:armed:${groupId}`);
                return v === null ? true : v === '1';
            } catch (e) {
                return true;
            }
        }

        #setArmed(groupId, value) {
            try {
                sessionStorage.setItem(`gaa:armed:${groupId}`, value ? '1' : '0');
            } catch (e) {
                /* sessionStorage no disponible (modo privado, etc.) */
            }
        }

        #esc(str) {
            return String(str == null ? '' : str)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
        }

        #showModal(groups) {
            if (!groups || !groups.length) return;
            const batches = [];
            const generalGroups = groups.filter((group) => !group.popup?.enabled);
            if (generalGroups.length) batches.push(generalGroups);
            for (const group of groups) {
                if (group.popup?.enabled) batches.push([group]);
            }
            this.modalQueue = (this.modalQueue || []).concat(batches);
            if (!this.modalEl) this.#showNextModal();
        }

        #showNextModal() {
            const groups = this.modalQueue?.shift();
            if (!groups) return;
            const popup = groups.length === 1 && groups[0].popup?.enabled ? groups[0].popup : null;
            const title = popup ? String(popup.title ?? '').trim() : this.popupTitle;
            const button = popup?.button || this.popupButton;
            const free = popup ? String(popup.free ?? '').trim() : this.popupFree;
            const extra = popup ? String(popup.extraText ?? '').trim() : this.popupExtraText;

            const names = groups.map((g) => g.name).filter(Boolean).join(', ');
            const heading = String(title).replace('{name}', names);

            const items = groups.map((g) => {
                const label = this.#esc(g.giftTitle || g.name || '');
                const itemName = popup ? this.#esc(String(popup.giftTitle ?? '').trim()) : label;
                const img = g.giftImage && (!popup || popup.showGiftImage !== false)
                    ? `<img class="gaa-modal__image" src="${this.#esc(g.giftImage)}" alt="${label}" loading="lazy">`
                    : '';
                const itemTitle = itemName || free
                    ? `<p class="gaa-modal__item-title">
                            ${itemName ? `<span class="gaa-modal__item-name">${itemName}</span>` : ''}
                            ${free ? `<span class="gaa-modal__free">${this.#esc(free)}</span>` : ''}
                        </p>`
                    : '';
                if (!img && !itemTitle) return '';
                return `
                    <div class="gaa-modal__item">
                        ${img}
                        ${itemTitle}
                    </div>`;
            }).join('');
            const extraText = extra
                ? `<p class="gaa-modal__extra-text">${this.#esc(popup ? String(extra).replace('{name}', names) : extra)}</p>`
                : '';

            const overlay = document.createElement('div');
            overlay.className = 'gaa-modal';
            overlay.setAttribute('role', 'dialog');
            overlay.setAttribute('aria-modal', 'true');
            overlay.setAttribute('aria-label', heading || names || this.popupTitle);
            overlay.innerHTML = `
                <div class="gaa-modal__backdrop" data-gaa-close></div>
                <div class="gaa-modal__card" role="document">
                    <button class="gaa-modal__close" type="button" data-gaa-close aria-label="Cerrar">&times;</button>
                    ${heading ? `<p class="gaa-modal__heading">${this.#esc(heading)}</p>` : ''}
                    ${items ? `<div class="gaa-modal__items">${items}</div>` : ''}
                    ${extraText}
                    <button class="gaa-modal__button" type="button" data-gaa-close>${this.#esc(button)}</button>
                </div>`;

            if (popup) {
                const card = overlay.querySelector('.gaa-modal__card');
                card.classList.add('gaa-modal__card--custom');
                if (popup.backgroundImage && !heading && !items && !extra) card.classList.add('gaa-modal__card--image-only');
                card.style.setProperty('--gaa-text-color', popup.textColor || '#1f1f1f');
                card.style.setProperty('--gaa-button-background', popup.buttonBackground || '#1f1f1f');
                card.style.setProperty('--gaa-button-text-color', popup.buttonTextColor || '#ffffff');
                const opacity = Number(popup.overlayOpacity ?? 80);
                const alpha = Number.isFinite(opacity) ? Math.max(0, Math.min(100, opacity)) / 100 : 0.8;
                card.style.setProperty('--gaa-image-overlay', `rgba(255, 255, 255, ${alpha})`);
                card.style.setProperty('--gaa-background-image', popup.backgroundImage ? `url(${JSON.stringify(popup.backgroundImage)})` : 'none');
                card.style.setProperty('--gaa-background-position', popup.backgroundPosition || 'center');
            }

            overlay.addEventListener('click', (event) => {
                if (event.target.closest('[data-gaa-close]')) this.#closeModal();
            });
            this.onKeydown = (event) => {
                if (event.key === 'Escape') this.#closeModal();
            };
            document.addEventListener('keydown', this.onKeydown);

            document.body.appendChild(overlay);
            document.body.classList.add('gaa-modal-open');
            this.modalEl = overlay;
            requestAnimationFrame(() => overlay.classList.add('gaa-modal--visible'));
        }

        #closeModal(advance = true) {
            if (!advance) this.modalQueue = [];
            if (this.onKeydown) {
                document.removeEventListener('keydown', this.onKeydown);
                this.onKeydown = null;
            }
            const el = this.modalEl;
            if (!el) return;
            this.modalEl = null;
            document.body.classList.remove('gaa-modal-open');
            el.classList.remove('gaa-modal--visible');
            el.style.pointerEvents = 'none';
            setTimeout(() => el.remove(), 300);
            if (advance) this.#showNextModal();
        }
    }

    if (!window.customElements.get('gift-auto-add')) {
        window.customElements.define('gift-auto-add', GiftAutoAdd);
    }
}

// ----------------------- Export para tests (Node) -----------------------

if (typeof module !== 'undefined' && module.exports) {
    module.exports = { countUnits, giftInCart, evaluateGroup };
}
